import React, { useState, useEffect } from 'react';
import { getPrompts, savePrompt } from '../api';
import { useSports } from '../hooks';
import { useAdminList } from '../hooks/useAdminList';
import { toast } from 'react-toastify';
import { commonStyles, colors, spacing } from '../styles/design-system';
import { User } from '../api/auth';

interface PromptManagerProps {
  currentUser: User;
}

const PromptManager: React.FC<PromptManagerProps> = ({ currentUser }) => {
  const isAdministrator = currentUser.role === 'administrator';
  const [loading, setLoading] = useState(false);
  const [prompts, setPrompts] = useState<any>({});
  const [selectedSport, setSelectedSport] = useState('');
  const [selectedVersion, setSelectedVersion] = useState<number>(0);
  const [editingPrompt, setEditingPrompt] = useState('');
  const [originalPrompt, setOriginalPrompt] = useState('');

  // 종목 데이터 - useSports 훅 사용
  const { sports, sportOptions, loading: sportsLoading } = useSports();

  // 관리자 목록 (administrator만 자동 조회 + 첫 admin 자동 선택)
  const { adminList } = useAdminList({
    immediate: isAdministrator,
    onSuccess: (list) => {
      if (list.length > 0) {
        setFilterAdminId(prev => prev === null ? list[0].admin_id : prev);
      }
    },
  });
  const [filterAdminId, setFilterAdminId] = useState<number | null>(null);

  // 종목 데이터 로딩 후 첫 번째 종목 선택
  useEffect(() => {
    if (sports.length > 0 && !selectedSport) {
      setSelectedSport(sports[0].sport_name);
    }
  }, [sports, selectedSport]);

  useEffect(() => {
    if (selectedSport) {
      loadPrompts();
    }
  }, []);

  useEffect(() => {
    if (isAdministrator) {
      loadPrompts();
    }
  }, [filterAdminId]);

  useEffect(() => {
    if (selectedSport && prompts[selectedSport]) {
      const versions = prompts[selectedSport].versions || [];
      let promptContent = '';
      if (versions.length > 0) {
        setSelectedVersion(0);
        promptContent = versions[0]?.template || prompts[selectedSport].current || '';
      } else {
        promptContent = prompts[selectedSport].current || '';
      }
      setEditingPrompt(promptContent);
      setOriginalPrompt(promptContent);
    }
  }, [selectedSport, prompts]);

  // selectedSport이 설정되면 프롬프트 로드
  useEffect(() => {
    if (selectedSport) {
      loadPrompts();
    }
  }, [selectedSport]);

  const loadPrompts = async () => {
    setLoading(true);
    try {
      const data = await getPrompts(isAdministrator ? filterAdminId : undefined);
      setPrompts(data);
      if (data[selectedSport]) {
        const versions = data[selectedSport].versions || [];
        if (versions.length > 0) {
          setEditingPrompt(versions[0]?.template || data[selectedSport].current || '');
        } else {
          setEditingPrompt(data[selectedSport].current || '');
        }
      }
    } catch (error) {
      console.error('프롬프트 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVersionChange = (versionIndex: number) => {
    setSelectedVersion(versionIndex);
    const versions = prompts[selectedSport]?.versions || [];
    if (versions[versionIndex]) {
      const template = versions[versionIndex].template;
      setEditingPrompt(template);
      setOriginalPrompt(template);
    }
  };

  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(editingPrompt);
    toast.success('프롬프트가 클립보드에 복사되었음.');
  };

  const handleSavePrompt = async () => {
    try {
      const targetAdminId = isAdministrator ? filterAdminId : undefined;
      const result = await savePrompt(selectedSport, { prompt: editingPrompt }, targetAdminId);

      const adminName = targetAdminId
        ? adminList.find(a => a.admin_id === targetAdminId)?.display_name || `관리자 ${targetAdminId}`
        : '본인';
      toast.success(`프롬프트가 저장되었음. (ID: ${result.prompt_id}, 대상: ${adminName})`);

      setOriginalPrompt(editingPrompt);
      await loadPrompts();
    } catch (error) {
      console.error('프롬프트 저장 실패:', error);
      toast.error('프롬프트 저장에 실패했음.');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>프롬프트 관리</h3>
        <div style={styles.scheduleForm}>
          {isAdministrator && (
            <div style={styles.formGroupVertical}>
              <label style={styles.label}>프롬프트 편집 대상</label>
              <select
                style={styles.select}
                value={filterAdminId ?? ''}
                onChange={(e) => setFilterAdminId(e.target.value ? Number(e.target.value) : null)}
              >
                {adminList.map(admin => (
                  <option key={admin.admin_id} value={admin.admin_id}>
                    {admin.display_name} ({admin.role === 'administrator' ? '관리자' : '매니저'})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div style={styles.formGroupVertical}>
            <label style={styles.label}>종목</label>
            <select
              style={styles.select}
              value={selectedSport}
              onChange={(e) => setSelectedSport(e.target.value)}
            >
              {sportOptions.map(sport => (
                <option key={sport.value} value={sport.value}>{sport.label}</option>
              ))}
            </select>
          </div>

          {prompts[selectedSport]?.versions && Array.isArray(prompts[selectedSport].versions) && prompts[selectedSport].versions.length > 0 && (
            <div style={styles.formGroupVertical}>
              <label style={styles.label}>버전</label>
              <select
                style={styles.select}
                value={selectedVersion}
                onChange={(e) => handleVersionChange(Number(e.target.value))}
              >
                {(prompts[selectedSport].versions || []).map((v: any, idx: number) => (
                  <option key={idx} value={idx}>
                    버전 {v.version} {idx === 0 ? '(최신)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <div style={styles.listSection}>
        <div style={{display: 'flex', alignItems: 'center', marginBottom: spacing[3]}}>
          <h3 style={{...styles.sectionTitle, marginBottom: 0, marginRight: spacing[3]}}>프롬프트 템플릿</h3>
          <button
            onClick={handleCopyPrompt}
            style={{...styles.button, ...styles.secondaryButton, padding: `${spacing[1]} ${spacing[3]}`}}
            disabled={loading || !editingPrompt}
          >
            복사
          </button>
          <div style={{flex: 1}} />
          <button
            onClick={handleSavePrompt}
            style={{
              ...styles.button,
              ...styles.primaryButton,
              padding: `${spacing[1]} ${spacing[3]}`,
              opacity: editingPrompt === originalPrompt ? 0.5 : 1
            }}
            disabled={loading || editingPrompt === originalPrompt}
          >
            새 버전으로 저장
          </button>
        </div>
        <div style={styles.promptEditSection}>
          <textarea
            value={editingPrompt}
            onChange={(e) => setEditingPrompt(e.target.value)}
            style={{
              ...styles.textarea,
              backgroundColor: colors.white
            }}
            rows={15}
            placeholder="프롬프트를 입력해야 함..."
          />
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[5],
    paddingBottom: spacing[10],
  },
  section: commonStyles.section,
  sectionTitle: commonStyles.sectionTitle,
  scheduleForm: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing[5],
    alignItems: 'flex-end',
  },
  formGroupVertical: commonStyles.formGroupVertical,
  label: commonStyles.label,
  select: commonStyles.select,
  listSection: {
    marginTop: spacing[7],
  },
  button: commonStyles.button,
  primaryButton: commonStyles.buttonPrimary,
  secondaryButton: commonStyles.buttonSecondary,
  promptEditSection: commonStyles.section,
  textarea: {
    ...commonStyles.textarea,
    height: '50vh',
    marginTop: spacing[3],
  },
};

export default PromptManager;
